import { type Instruction } from "@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item";
import { makeAutoObservable } from "mobx";
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useState,
} from "react";
import { assert, orderUtilsMutable } from "utils";

import { useWorkoutEditStore } from "./WorkoutEditContext";
import {
  type WorkoutEditStore,
  type WorkoutItemLayout,
} from "./WorkoutEditStore";

const WORKOUT_ITEMS_DRAG_AND_DROP_MANAGER_CONTEXT_ID = Symbol.for(
  "WORKOUT_ITEMS_DRAG_AND_DROP_MANAGER_CONTEXT_ID",
);

interface UpdateItemsAction {
  instruction: Instruction;
  itemId: string;
  targetId: string;
}

interface ActiveDropTarget {
  targetId: string;
  instruction: Instruction;
}

function collectAllGroupIds(items: WorkoutItemLayout[]): string[] {
  const ids: string[] = [];

  for (const item of items) {
    if (item.type === "GROUP") {
      ids.push(item.workoutItemId);

      if (item.children) {
        ids.push(...collectAllGroupIds(item.children));
      }
    }
  }

  return ids;
}

class WorkoutItemsDragAndDropManager {
  accordionItems: string[] = [];
  workoutEditStore: WorkoutEditStore;
  activeDropTarget: ActiveDropTarget | null = null;

  constructor(workoutEditStore: WorkoutEditStore) {
    this.workoutEditStore = workoutEditStore;
    this.accordionItems = collectAllGroupIds(workoutEditStore.layout.items);
    makeAutoObservable(this, {
      workoutEditStore: true,
    });
  }

  setActiveDropTarget(targetId: string, instruction: Instruction) {
    this.activeDropTarget = { targetId, instruction };
  }

  clearActiveDropTarget() {
    this.activeDropTarget = null;
  }

  getInstructionForTarget(targetId: string): Instruction | null {
    if (this.activeDropTarget?.targetId === targetId) {
      return this.activeDropTarget.instruction;
    }

    return null;
  }

  get accordionValue() {
    return this.accordionItems;
  }

  get id() {
    return WORKOUT_ITEMS_DRAG_AND_DROP_MANAGER_CONTEXT_ID;
  }

  get horizontalIndent() {
    return 32;
  }

  toggleAll(ids: string[]) {
    this.accordionItems = ids;
  }

  openAccordionItem(id: string) {
    if (!this.accordionItems.includes(id)) {
      this.accordionItems.push(id);
    }
  }

  openAccordionItems(ids: string[]) {
    for (const id of ids) {
      this.openAccordionItem(id);
    }
  }

  closeAccordionItem(id: string) {
    this.accordionItems = this.accordionItems.filter((item) => item !== id);
  }

  isOpen(id: string) {
    return this.accordionItems.includes(id);
  }

  updateItems(action: UpdateItemsAction) {
    console.log(action);

    if (
      (action.itemId === action.targetId &&
        action.instruction.type !== "reparent") ||
      action.instruction.type === "instruction-blocked"
    ) {
      return;
    }

    let targetId = action.targetId;

    if (action.instruction.type === "reparent") {
      const desiredTarget = this.workoutEditStore.getWorkoutItemById(
        action.targetId,
      );

      assert(
        desiredTarget.parentId !== null,
        "desiredTarget.parentId is null boy",
      );

      targetId = desiredTarget.parentId;
    }

    const item = this.workoutEditStore.getWorkoutItemById(action.itemId);
    const target = this.workoutEditStore.getWorkoutItemById(targetId);

    if (!item || !target) {
      return;
    }

    const sourceGroup =
      item.parentId === null
        ? this.workoutEditStore.rootWorkoutItems
        : (this.workoutEditStore.workoutItemsByParentId[item.parentId] ?? []);
    const destGroup =
      action.instruction.type === "make-child"
        ? (this.workoutEditStore.workoutItemsByParentId[target.id] ?? [])
        : target.parentId === null
          ? this.workoutEditStore.rootWorkoutItems
          : (this.workoutEditStore.workoutItemsByParentId[target.parentId] ??
            []);

    orderUtilsMutable.moveByInstruction(
      item,
      target,
      sourceGroup,
      destGroup,
      action.instruction.type,
      "parentId",
    );
  }
}

export const workoutItemsDragAndDropManagerContext =
  createContext<WorkoutItemsDragAndDropManager | null>(null);

export function useWorkoutItemsDragAndDropManager() {
  const context = useContext(workoutItemsDragAndDropManagerContext);
  assert(
    context !== null,
    "useWorkoutItemsDragAndDropManager must be used within a WorkoutItemsDragAndDropManagerContext",
  );
  return context;
}

export function WorkoutItemsDragAndDropManagerProvider(
  props: PropsWithChildren,
) {
  const workoutEditStore = useWorkoutEditStore();

  const [manager] = useState(() => {
    return new WorkoutItemsDragAndDropManager(workoutEditStore);
  });

  return (
    <workoutItemsDragAndDropManagerContext.Provider value={manager}>
      {props.children}
    </workoutItemsDragAndDropManagerContext.Provider>
  );
}
